import { useEffect } from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import { LazyVideoFrame } from "../components/VideoFrame";
import { PROJECTS } from "../lib/projects";

function PortfolioCard({ project, index }) {
  const isFeatured = project.featured;
  const isInProgress = project.status === "In Progress";
  const hasStats = Array.isArray(project.stats) && project.stats.length > 0;
  const hasOutcome = Boolean(project.outcome);
  const hasRightPanel = hasStats || hasOutcome;
  const useTwoCol = isFeatured && hasRightPanel;

  return (
    <article
      className={`work-card ${isFeatured ? "md:col-span-12" : "md:col-span-6"} col-span-12`}
      data-testid={`work-card-${index + 1}`}
    >
      <LazyVideoFrame
        src={project.video}
        poster={project.poster}
        title={project.title}
        aspect="aspect-video"
        eager={index === 0}
      />

      <div className={`p-7 md:p-9 ${useTwoCol ? "grid md:grid-cols-12 gap-6 md:gap-10" : ""}`}>
        <div className={`${useTwoCol ? "md:col-span-7" : ""}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="eyebrow">{project.category}</span>
            {isInProgress ? (
              <span className="pulse-live" data-testid={`work-status-${index + 1}`}>
                In Progress
              </span>
            ) : (
              <span
                className="text-xs uppercase tracking-widest text-teal"
                data-testid={`work-status-${index + 1}`}
              >
                Live
              </span>
            )}
          </div>
          <h3 className={`${isFeatured ? "display-h2" : "display-h3"} mb-5 max-w-3xl`}>
            {project.title}
          </h3>
          <p className="text-muted leading-relaxed max-w-prose">{project.description}</p>
        </div>

        {useTwoCol && hasStats ? (
          <div
            className="md:col-span-5 md:border-l border-hair/20 md:pl-8"
            data-testid={`work-stats-${index + 1}`}
          >
            <div className="eyebrow mb-5">
              {isInProgress ? "Projected impact" : "By the numbers"}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-7">
              {project.stats.map((s, k) => (
                <div key={k}>
                  <div className="font-display font-bold text-ink text-3xl md:text-4xl leading-none mb-1.5">
                    {s.value}
                  </div>
                  <div className="text-sm text-ink/85 leading-tight">{s.label}</div>
                  <div className="text-xs text-muted mt-1">{s.caption}</div>
                </div>
              ))}
          </div>
        </div>
        ) : useTwoCol && hasOutcome ? (
          <div className="md:col-span-5 md:border-l border-hair/20 md:pl-8">
            <div className="eyebrow mb-3">Outcome</div>
            <p className="text-ink/90 leading-relaxed">{project.outcome}</p>
          </div>
        ) : !isFeatured && hasStats ? (
          <div className="mt-6 pt-6 hairline-t" data-testid={`work-stats-${index + 1}`}>
            <div className="eyebrow mb-4">
              {isInProgress ? "Projected impact" : "By the numbers"}
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-5">
              {project.stats.map((s, k) => (
                <div key={k}>
                  <div className="font-display font-bold text-ink text-2xl md:text-[1.65rem] leading-none mb-1.5">
                    {s.value}
                  </div>
                  <div className="text-[0.8125rem] text-ink/85 leading-tight">{s.label}</div>
                  <div className="text-xs text-muted mt-0.5">{s.caption}</div>
                </div>
              ))}
            </div>
          </div>
        ) : !isFeatured && hasOutcome ? (
          <div className="mt-6 pt-6 hairline-t">
            <div className="eyebrow mb-3">Outcome</div>
            <p className="text-ink/90 leading-relaxed">{project.outcome}</p>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default function Work() {
  useEffect(() => {
    document.title = "Work — Socialize Video";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-bg" data-testid="work-page">
      <section className="pt-12 md:pt-16 pb-10 md:pb-12" data-testid="work-header">
        <div className="container-x grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="eyebrow mb-4">Work</div>
            <h1 className="display-h2 mb-4">The Work</h1>
            <p className="text-base md:text-lg text-ink/75 max-w-2xl leading-relaxed">
              Every build is documented. Here's what we've shipped.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <p className="text-sm text-muted">A short, curated list. New builds added monthly.</p>
          </div>
        </div>
      </section>

      <section className="pb-20" data-testid="work-grid-section">
        <div className="container-x">
          <Reveal staggerChildrenMs={120} className="grid grid-cols-12 gap-6 md:gap-8">
            {PROJECTS.map((p, i) => (
              <div
                key={p.slug}
                className={`reveal ${p.featured ? "md:col-span-12" : "md:col-span-6"} col-span-12`}
              >
                <PortfolioCard project={p} index={i} />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section-dark py-24 md:py-32" data-testid="work-cta-section">
        <div className="container-x max-w-3xl">
          <Reveal>
            <p className="display-h2 mb-10">
              Portfolio is actively growing. Apply now to be featured in the next documented build.
            </p>
            <Link to="/apply" className="cta" data-testid="work-apply-cta">
              Apply for a Build <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
